import cloneDeep from "lodash/cloneDeep";
import map from "lodash/map";
import filter from "lodash/filter";
import sortBy from "lodash/sortBy";
import find from "lodash/find";
import uniq from "lodash/uniq";
import sumBy from "lodash/sumBy";
import isUndefined from "lodash/isUndefined";
import store from "store";
import * as Sentry from "@sentry/browser";
import { returnRubberBandArray } from "@/store/utils/rubberBands";
import { returnWithVideosSpaced } from "@/store/utils/rubberVideosOnly.js";
import { returnSlideArrayWithGroupedSlidesAdjusted } from "@/store/utils/groupSlideMoves.js";
import { returnSlideArrayWithMovementData } from "@/components/SoundslidesPlayer/motionUtils";

const ensureArrayHasUniqueIds = _array => {
  var existing_ids = [];
  return map(_array, s => {
    if (!s.id || existing_ids.includes(s.id)) {
      s.id = Math.random()
        .toString(36)
        .substring(7);
    }
    existing_ids.push(s.id);
    return s;
  });
};

const ensureOverlaysAreValid = _array => {
  // Make sure overlay array has the start button.
  var startButton = find(_array, { type: "start_button" });
  if (!startButton) {
    _array.push({
      duration: 1,
      enabled: true,
      id: "startButtonId",
      inpoint: 0,
      interactive: true,
      layer: 3,
      transition_seconds: 0,
      type: "start_button"
    });
  }
  return sortBy(
    map(_array, o => {
      // All overlays need transition_seconds.
      if (isUndefined(o.transition_seconds)) {
        o.transition_seconds = 1;
      }
      // All overlays need a layer (default is 3)
      if (isUndefined(o.layer)) {
        o.layer = 3;
      }
      return o;
    }),
    "inpoint"
  );
};

const ensureVideoSlidesHaveValidDataForEditing = (slideArray, show_object) => {
  /*
  Items to maintain on video slides in the slideArray.
  1. Files AFTER videos should be unpinned.
  2. All video slides should have a transition_time_in and transition_time_out.
  */

  return map(slideArray, (s, idx) => {
    if (s.type == "video") {
      // transition_duration or globalTransitionTime.
      var transition_time_in =
        show_object.data.displaySettings.globalTransitionTime;
      if (
        show_object.data.displaySettings.globalTransitionType == "straight cut"
      ) {
        transition_time_in = 0;
      }

      if (s.transition == "straight cut") {
        transition_time_in = 0;
      } else if (
        s.transition == "crossfade" ||
        s.transition == "fade out/fade in"
      ) {
        transition_time_in = s.transition_duration;
      }

      if (s.timing == 0) {
        // All slides at timing=0 and no specific transition, have no inbound transition.
        transition_time_in = 0;
      }

      s.transition_time_in = transition_time_in / 2;

      // next image's transition_duration or globalTransitionTime.
      var transition_time_out =
        show_object.data.displaySettings.globalTransitionTime;
      if (
        show_object.data.displaySettings.globalTransitionType == "straight cut"
      ) {
        transition_time_out = 0;
      }

      if (idx + 1 < slideArray.length) {
        var next_slide = slideArray[idx + 1];
        if (next_slide.transition == "straight cut") {
          transition_time_out = 0;
        } else if (
          next_slide.transition == "crossfade" ||
          next_slide.transition == "fade out/fade in"
        ) {
          transition_time_out = next_slide.transition_duration;
        }
        s.transition_time_out = transition_time_out / 2;
        // Make sure slide is unpinned.
        next_slide.unpinned = true; // I don't think this does what I think it does.
      }

      s.transition_time_out = transition_time_out / 2;

      // Make sure keep_sync is defined.
      if (s.keep_sync !== false) {
        s.keep_sync = true;
      }
    }
    return s;
  });
};

const isShowVideoExportOnly = _slideArray => {
  // console.log("slideArray", slideArray);
  var slideArray = cloneDeep(_slideArray); // cloneDeep as not to add anything during evaluation.
  slideArray = returnSlideArrayWithMovementData(slideArray);
  var slidesWithoutMotion = filter(slideArray, {
    end_percent_scale: 100,
    end_percent_x: 0,
    end_percent_y: 0,
    start_percent_scale: 100,
    start_percent_x: 0,
    start_percent_y: 0
  });
  var showHasMotion = slidesWithoutMotion.length != slideArray.length;
  var showHasVideos = sumBy(slideArray, { type: "video" }) > 0;
  console.log("showHasMotion", showHasMotion);
  console.log("showHasVideos", showHasVideos);
  if (showHasMotion || showHasVideos) {
    return true;
  } else {
    return false;
  }
};

export default {
  setShows(state, shows) {
    state.shows = shows;
  },
  setShowDetails(state, details) {
    console.log("setShowDetails");
    // Enforce that data.displaySettings exists.
    if (!details.data.displaySettings) {
      details.data.displaySettings = {
        globalTransitionType: "crossfade",
        globalTransitionTime: 1
      };
    }
    // Ensure that the displaySettings relating to the timeline modes.
    details.data.displaySettings.globalTimelineElasticMode =
      details.data.displaySettings.globalTimelineElasticMode || false;
    details.data.displaySettings.globalTimelineAutoPin =
      details.data.displaySettings.globalTimelineAutoPin || false;

    details.data.slideArray = ensureArrayHasUniqueIds(details.data.slideArray);
    details.data.overlayArray = ensureArrayHasUniqueIds(
      details.data.overlayArray
    );
    details.data.overlayArray = ensureOverlaysAreValid(
      details.data.overlayArray
    );
    state.currentShowDetails = details;
    state.videoExportOnly = isShowVideoExportOnly(details.data.slideArray);
    // Store image metadata.
    var imageMetadataItems = filter(details, (s, k) => {
      if (k.startsWith("metadataImages_")) {
        return s;
      }
    });
    state.imageMetadataItems = imageMetadataItems;
    if (state.currentShowHistory.length == 0) {
      // Save history.
      var history_object = {
        time: new Date().getTime(),
        showDetails: cloneDeep(state.currentShowDetails)
      };
      state.currentShowHistory.push(history_object);
    }
  },
  setCurrentId(state, id) {
    state.currentShowId = id;
  },
  setGroupedSlideIds(state, groupedSlideIds) {
    state.groupedSlideIds = groupedSlideIds;
  },
  setActivePropertyItem(state, item) {
    state.activePropertyItem = item;
  },
  setCurrentSlideIndex(state, slideIndex) {
    state.currentSlideIndex = slideIndex;
  },
  setUser(state, user) {
    state.user = user;
    state.userAuthInitialized = true;
    Sentry.configureScope(scope => {
      scope.setUser({
        email: state.user.email,
        id: state.user.uid
      });
    });
  },
  setUserPrefs(state, prefs) {
    state.user_prefs = prefs;
    if (prefs.overlayFontOptions) {
      state.overlay_font_options = uniq(
        state.overlay_font_options.concat(prefs.overlayFontOptions)
      );
    }
  },
  addShow(state, show) {
    state.shows.push(show);
    store.set("shows", state.shows);
  },
  set_editor_shows(state, shows) {
    state.editor_shows = shows;
    state.showsLoaded = true;
  },
  saveSlideArrayToShow(state, slideArray) {
    // Use this one when you need to push a change to the show.
    //console.log("saveSlideArrayToShow");
    slideArray = ensureVideoSlidesHaveValidDataForEditing(
      ensureArrayHasUniqueIds(slideArray),
      state.currentShowDetails
    );

    var total_duration_ms = state.currentShowDetails.data.audio.duration * 1000;

    // Check for grouped slides here.
    var existingSlideArray = cloneDeep(
      state.currentShowDetails.data.slideArray
    );
    slideArray = returnSlideArrayWithGroupedSlidesAdjusted(
      slideArray,
      existingSlideArray,
      state.groupedSlideIds,
      total_duration_ms
    );

    // Rubber banding here.
    if (
      state.currentShowDetails.data.displaySettings.globalTimelineElasticMode
    ) {
      slideArray = returnRubberBandArray(slideArray, total_duration_ms);
    } else {
      slideArray = returnWithVideosSpaced(
        slideArray,
        total_duration_ms,
        state.currentShowDetails.data.displaySettings.timelinePushMode
      );
    }

    state.currentShowDetails.data.slideArray = slideArray;
    state.videoExportOnly = isShowVideoExportOnly(slideArray);
    state.editVersionCount++;
  },
  saveOverlayArrayToShow(state, overlayArray) {
    // Use this one when you need to push a change to the show.
    console.log("saveOverlayArrayToShow");
    state.currentShowDetails.data.overlayArray = overlayArray;
    state.editVersionCount++;
  },
  savedisplaySettingsToShow(state, displaySettings) {
    // Use this one when you need to push a change to the show.
    // This needs to MERGE in order to not overwrite any imported displaySettings,
    // that may not be currently supported by the editor.
    console.log("savedisplaySettingsToShow");
    state.currentShowDetails.data.displaySettings = Object.assign(
      state.currentShowDetails.data.displaySettings,
      displaySettings
    );
    state.editVersionCount++;
  },
  addHistory(state) {
    var history_object = {
      time: new Date().getTime(),
      showDetails: cloneDeep(state.currentShowDetails)
    };
    state.currentShowHistory.push(history_object);
  },
  setEditPaneMode(state, mode_name) {
    state.edit_pane_mode = mode_name;
  },
  setPropertyMode(state, mode_name) {
    state.property_mode = mode_name;
  },
  switchLayout(state) {
    if (state.layoutIndex < state.layoutFunctions.length - 1) {
      state.layoutIndex = state.layoutIndex + 1;
    } else {
      state.layoutIndex = 0;
    }
  },
  setEditorAudioReference(state, _ref) {
    state.editorAudioReference = _ref;
  },
  setBowser(state, _bowser) {
    state.bowser = _bowser;
  },
  pendingSave(state) {
    state.pendingSave = true;
  },
  saveCompleted(state) {
    state.pendingSave = false;
  }
};
